import React, { useState } from 'react';
import { EvidencePackage, MoveCategory } from '@chess-platform/shared';
import {
    Sparkles, ChevronDown, ChevronUp, ArrowRight,
    Star, AlertTriangle, Zap, BookOpen, Info, Target
} from 'lucide-react';

// ── Quality badge config ──────────────────────────────────────────────────────

interface QualityConfig {
    label: string;
    emoji: string;
    color: string;
    bg: string;
    border: string;
}

const QUALITY_CONFIG: Record<string, QualityConfig> = {
    brilliant: { label: 'BRILLANTE', emoji: '!!', color: '#a78bfa', bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.3)' },
    great: { label: 'GENIAL', emoji: '!', color: '#34d399', bg: 'rgba(52,211,153,0.12)', border: 'rgba(52,211,153,0.3)' },
    best: { label: 'MEJOR', emoji: '★', color: '#22c55e', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.3)' },
    excellent: { label: 'EXCELENTE', emoji: '✓', color: '#4ade80', bg: 'rgba(74,222,128,0.12)', border: 'rgba(74,222,128,0.3)' },
    good: { label: 'BUENA', emoji: '·', color: '#93c5fd', bg: 'rgba(147,197,253,0.1)', border: 'rgba(147,197,253,0.2)' },
    book: { label: 'TEORÍA', emoji: '📖', color: '#60a5fa', bg: 'rgba(96,165,250,0.1)', border: 'rgba(96,165,250,0.2)' },
    inaccuracy: { label: 'IMPRECISIÓN', emoji: '?!', color: '#fbbf24', bg: 'rgba(251,191,36,0.1)', border: 'rgba(251,191,36,0.25)' },
    mistake: { label: 'ERROR', emoji: '?', color: '#f97316', bg: 'rgba(249,115,22,0.1)', border: 'rgba(249,115,22,0.25)' },
    blunder: { label: 'GAFFE', emoji: '??', color: '#ef4444', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)' },
    forced: { label: 'FORZADA', emoji: '□', color: '#94a3b8', bg: 'rgba(148,163,184,0.08)', border: 'rgba(148,163,184,0.2)' },
    analysing: { label: 'ANALIZANDO...', emoji: '●', color: '#cbd5e1', bg: 'rgba(203,213,225,0.05)', border: 'rgba(203,213,225,0.1)' },
};

// ── Severity icon ─────────────────────────────────────────────────────────────

function SeverityIcon({ severity }: { severity: 'low' | 'medium' | 'high' }) {
    const color = severity === 'high' ? '#ef4444' : severity === 'medium' ? '#f97316' : '#fbbf24';
    return <span style={{ color }} className="text-[10px] font-black">{'▲'.repeat(severity === 'high' ? 3 : severity === 'medium' ? 2 : 1)}</span>;
}

// ── Eval display ──────────────────────────────────────────────────────────────

function EvalDisplay({ point }: { point: EvidencePackage['engine']['evalAfter'] }) {
    if (point.type === 'mate') {
        const sign = point.value > 0 ? '' : '-';
        return <span className="font-black text-yellow-400">#{sign}{Math.abs(point.value)}</span>;
    }
    const pawns = (point.value / 100).toFixed(1);
    const color = point.value >= 0 ? '#22c55e' : '#ef4444';
    return <span className="font-black" style={{ color }}>{point.value >= 0 ? '+' : ''}{pawns}</span>;
}

// ── Main CoachPanel component ─────────────────────────────────────────────────

interface CoachPanelProps {
    evidence: EvidencePackage;
    onHoverArrow?: (arrows: [string, string, string][]) => void;
    onLeaveArrow?: () => void;
    /** Called when the user clicks the AI coach button */
    onAskCoach?: () => void;
    coachExplanation?: {
        short: string;
        details: string[];
        ifInstead?: string;
        warnings?: string[];
    } | null;
    coachLoading?: boolean;
}

const CoachPanel: React.FC<CoachPanelProps> = ({
    evidence,
    onAskCoach,
    coachExplanation,
    coachLoading = false,
}) => {
    const [showDetails, setShowDetails] = useState(false);

    const q = QUALITY_CONFIG[evidence.quality.label] ?? QUALITY_CONFIG.good;
    const isBadMove = evidence.quality.label === 'blunder' || evidence.quality.label === 'mistake' || evidence.quality.label === 'inaccuracy';
    const isBookMove = evidence.quality.label === 'book';

    // If-Instead: best alternative lines (only show if different from played)
    const hasBestAlternative = evidence.engine.bestMoveUci &&
        evidence.engine.bestMoveUci.slice(0, 4) !== evidence.moveUci.slice(0, 4);

    const bestLine = evidence.engine.lines.find(l => l.multipv === 1);
    const altLines = evidence.engine.lines.filter(l => l.multipv > 1);

    return (
        <div
            className="rounded-2xl overflow-hidden shadow-xl animate-in slide-in-from-right-4 duration-300"
            style={{ background: q.bg, border: `1px solid ${q.border}` }}
        >
            {/* ── Header: quality badge + move ── */}
            <div className="flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2 lg:py-3 border-b border-white/5">
                <div
                    className="flex items-center gap-1 lg:gap-1.5 px-2 py-0.5 lg:py-1 rounded-lg text-[9px] lg:text-[11px] font-black tracking-widest"
                    style={{ background: q.bg, color: q.color, border: `1px solid ${q.border}` }}
                >
                    <span>{q.emoji}</span>
                    <span className="hidden sm:inline">{q.label}</span>
                </div>

                <div className="flex-1 flex items-center gap-1.5 lg:gap-2">
                    <span className="text-white font-bold text-xs lg:text-sm">{evidence.moveSan}</span>
                    <span className="text-white/30 text-[9px] lg:text-xs">ply {evidence.ply}</span>
                </div>

                <div className="text-right">
                    <div className="text-[8px] lg:text-[10px] text-white/30 mb-0.5">eval</div>
                    <div className="text-[10px] lg:text-xs">
                        <EvalDisplay point={evidence.engine.evalAfter} />
                    </div>
                </div>
            </div>

            {/* ── Opening banner ── */}
            {evidence.opening && (
                <div className="flex items-center gap-1.5 px-3 lg:px-4 py-1.5 bg-blue-500/5 border-b border-blue-500/10">
                    <BookOpen size={10} className="text-blue-400 shrink-0" />
                    <span className="text-[9px] lg:text-[10px] font-bold text-blue-300 truncate max-w-[200px]">{evidence.opening.name}</span>
                </div>
            )}

            {/* ── Body ── */}
            <div className="p-3 lg:p-4 flex flex-col gap-2 lg:gap-3">

                {/* Loss indicator for bad moves */}
                {isBadMove && evidence.quality.lossCp > 0 && (
                    <div className="flex items-center gap-2 bg-red-500/10 rounded-lg lg:rounded-xl px-2.5 py-1.5 lg:px-3 lg:py-2 border border-red-500/15">
                        <AlertTriangle size={12} className="text-red-400 shrink-0" />
                        <span className="text-[10px] lg:text-[11px] text-red-300 font-semibold truncate">
                            -{(evidence.quality.lossCp / 100).toFixed(1)} peones
                        </span>
                    </div>
                )}

                {/* Motifs */}
                {evidence.motifs.length > 0 && (
                    <div className="flex flex-col gap-1 lg:gap-1.5">
                        {evidence.motifs.slice(0, 2).map((m, i) => (
                            <div key={i} className="flex items-start gap-2 bg-white/3 rounded-lg px-2.5 py-1.5 lg:px-3 lg:py-2">
                                <SeverityIcon severity={m.severity} />
                                <span className="text-[10px] lg:text-[11px] text-white/75 leading-tight lg:leading-relaxed">{m.hint}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Best alternative (If-Instead) */}
                {hasBestAlternative && bestLine && (
                    <div className="bg-yellow-500/5 border border-yellow-500/15 rounded-lg lg:rounded-xl p-2.5 lg:p-3">
                        <div className="flex items-center gap-2 mb-1">
                            <Target size={10} className="text-yellow-400" />
                            <span className="text-[9px] font-black text-yellow-400 uppercase tracking-widest">Mejor jugada</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-yellow-300 font-bold text-xs lg:text-sm">
                                {evidence.engine.bestMoveUci.slice(0, 2)}→{evidence.engine.bestMoveUci.slice(2, 4)}
                            </span>
                            <span className="ml-auto text-[10px] lg:text-xs">
                                <EvalDisplay point={{ type: 'cp', value: bestLine.score.value }} />
                            </span>
                        </div>
                    </div>
                )}

                {/* AI Coach button */}
                {onAskCoach && (
                    <button
                        onClick={onAskCoach}
                        disabled={coachLoading}
                        className="w-full flex items-center justify-center gap-2 py-1.5 lg:py-2 rounded-lg lg:rounded-xl text-[9px] lg:text-[10px] font-black uppercase tracking-widest transition-all
                            bg-[var(--primary)]/10 text-[var(--primary)] border border-[var(--primary)]/20
                            hover:bg-[var(--primary)]/20 hover:border-[var(--primary)]/40 disabled:opacity-40"
                    >
                        <Sparkles size={10} className={coachLoading ? 'animate-spin' : ''} />
                        {coachLoading ? '...' : (coachExplanation ? 'Re-analizar' : 'IA Coach')}
                    </button>
                )}

                {/* AI coach explanation */}
                {coachExplanation && (
                    <div className="bg-[var(--primary)]/5 border border-[var(--primary)]/15 rounded-lg lg:rounded-xl p-2.5 lg:p-3 animate-in fade-in zoom-in duration-500 flex flex-col gap-3">
                        <div className="flex items-center gap-1.5">
                            <Sparkles size={10} className="text-[var(--primary-bright)]" />
                            <span className="text-[8px] font-black text-white/40 uppercase tracking-widest">Maestro Virtual</span>
                        </div>

                        <div className="text-[10px] lg:text-[11px] font-bold text-[var(--primary-bright)] leading-relaxed">
                            {coachExplanation.short}
                        </div>

                        {coachExplanation.details.length > 0 && (
                            <ul className="text-[9px] lg:text-[10px] space-y-1 text-white/70 list-disc list-inside">
                                {coachExplanation.details.map((detail, i) => (
                                    <li key={i}>{detail}</li>
                                ))}
                            </ul>
                        )}

                        {coachExplanation.ifInstead && (
                            <div className="mt-1 p-2 bg-yellow-400/5 rounded-lg border border-yellow-400/10">
                                <span className="text-[8px] font-black text-yellow-400 uppercase tracking-widest block mb-1">Si hubieras jugado...</span>
                                <p className="text-[9px] lg:text-[10px] text-yellow-100/80 leading-tight italic">
                                    {coachExplanation.ifInstead}
                                </p>
                            </div>
                        )}

                        {coachExplanation.warnings && coachExplanation.warnings.length > 0 && (
                            <div className="mt-1 flex flex-col gap-1">
                                {coachExplanation.warnings.map((w, i) => (
                                    <div key={i} className="flex items-center gap-1.5 text-[9px] text-red-400 font-bold">
                                        <AlertTriangle size={8} />
                                        <span>{w}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CoachPanel;
